import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { app } from '../../firebase';
import { toast } from 'react-toastify';
import {
  FaSearch,
  FaEye,
  FaBriefcase,
  FaCalendar,
  FaUserAlt,
  FaClock,
  FaDollarSign,
  FaMapMarkerAlt,
  FaArrowLeft,
  FaDownload,
  FaUsers,
  FaCheckCircle,
  FaClock as FaPending
} from 'react-icons/fa';

const AdminJobApplications = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedJobId, setSelectedJobId] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    reviewedApplications: 0,
    totalJobs: 0
  });

  const auth = getAuth(app);
  const db = getDatabase(app);
  const navigate = useNavigate();

  const fetchJobsAndApplications = useCallback(async () => {
    try {
      // Fetch jobs, employers, and candidates in one round trip each instead
      // of one `get()` per employer and per applicant — the old version did
      // a separate sequential database call for every application, which
      // meant hundreds of round trips before this page could render.
      const [jobsSnapshot, employersSnapshot, candidatesSnapshot] = await Promise.all([
        get(ref(db, 'jobs')),
        get(ref(db, 'employers')),
        get(ref(db, 'candidates')),
      ]);

      if (!jobsSnapshot.exists()) {
        setLoading(false);
        return;
      }

      const employersData = employersSnapshot.exists() ? employersSnapshot.val() : {};
      const candidatesData = candidatesSnapshot.exists() ? candidatesSnapshot.val() : {};

      const allJobs = [];
      let totalApps = 0;
      let pendingApps = 0;
      let reviewedApps = 0;

      // Fetch all jobs and their applications
      for (const [employerId, employerJobs] of Object.entries(jobsSnapshot.val())) {
        const employerData = employersData[employerId]?.profile || null;

        for (const [jobId, jobData] of Object.entries(employerJobs)) {
          const applications = [];

          if (jobData.applications) {
            for (const [candidateId, appData] of Object.entries(jobData.applications)) {
              const candidateData = candidatesData[candidateId];

              if (candidateData) {
                applications.push({
                  candidateId,
                  jobQuestions: jobData.questions || null,
                  ...appData,
                  candidate: {
                    firstName: candidateData.firstName || '',
                    lastName: candidateData.lastName || '',
                    email: candidateData.email || '',
                    phone: candidateData.phone || '',
                    parish: candidateData.parish || '',
                    profile: candidateData.profile || {}
                  }
                });

                totalApps++;
                if (appData.status === 'pending') pendingApps++;
                if (appData.status === 'reviewed') reviewedApps++;
              }
            }
          }

          if (applications.length > 0) {
            allJobs.push({
              id: jobId,
              employerId,
              ...jobData,
              employer: employerData,
              applications: applications.sort((a, b) => b.appliedAt - a.appliedAt)
            });
          }
        }
      }

      // Sort jobs by most recent application
      allJobs.sort((a, b) => {
        const aLatest = a.applications[0]?.appliedAt || 0;
        const bLatest = b.applications[0]?.appliedAt || 0;
        return bLatest - aLatest;
      });

      setJobs(allJobs);
      setStats({
        totalApplications: totalApps,
        pendingApplications: pendingApps,
        reviewedApplications: reviewedApps,
        totalJobs: allJobs.length
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading applications');
      setLoading(false);
    }
  }, [db]);

  // Check admin access
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!auth.currentUser) {
        navigate('/admin-login');
        return;
      }

      try {
        const adminRef = ref(db, `admins/${auth.currentUser.uid}`);
        const snapshot = await get(adminRef);

        if (snapshot.exists()) {
          setIsAdmin(true);
          fetchJobsAndApplications();
        } else {
          toast.error('Access denied. Admin privileges required.');
          navigate('/admin-login');
        }
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/admin-login');
      }
    };

    checkAdminAccess();
  }, [auth.currentUser, db, navigate, fetchJobsAndApplications]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'reviewed':
        return 'bg-blue-100 text-blue-800';
      case 'shortlisted':
        return 'bg-green-100 text-green-800';
      case 'hired':
        return 'bg-green-600 text-white';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const filteredJobs = jobs.filter(job => {
    // Search filter
    const matchesSearch =
      job.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.employer?.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    // Job filter
    const matchesJob = selectedJobId === 'all' || job.id === selectedJobId;

    // Status filter
    let matchesStatus = true;
    if (filterStatus !== 'all') {
      matchesStatus = job.applications.some(app => app.status === filterStatus);
    }

    return matchesSearch && matchesJob && matchesStatus;
  });

  // Reset to page 1 when filters or page size change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterStatus, selectedJobId, pageSize]);

  const exportToCSV = () => {
    const csvData = [];
    filteredJobs.forEach(job => {
      job.applications.forEach(app => {
        csvData.push({
          'Job Title': job.jobTitle,
          'Company': job.companyName || job.employer?.companyName || 'N/A',
          'Candidate Name': `${app.candidate.firstName} ${app.candidate.lastName}`,
          'Email': app.candidate.email,
          'Phone': app.candidate.phone,
          'Parish': app.candidate.parish,
          'Applied Date': new Date(app.appliedAt).toLocaleDateString(),
          'Status': app.status,
          'Application ID': app.applicationId || 'N/A'
        });
      });
    });

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-applications-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isAdmin || loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Link to="/admin" className="text-blue-950 hover:text-blue-700">
            <FaArrowLeft size={20} />
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-blue-950 flex items-center">
              <FaBriefcase className="mr-2" />
              Job Applications
            </h2>
            <p className="text-gray-600">
              Monitor all job applications across the platform
            </p>
          </div>
        </div>

        <button
          onClick={exportToCSV}
          disabled={filteredJobs.length === 0}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaDownload />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Applications</p>
              <p className="text-2xl font-bold text-blue-950">{stats.totalApplications}</p>
            </div>
            <FaUsers className="text-blue-600" size={32} />
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">Pending</p>
              <p className="text-2xl font-bold text-yellow-800">{stats.pendingApplications}</p>
            </div>
            <FaPending className="text-yellow-600" size={32} />
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Reviewed</p>
              <p className="text-2xl font-bold text-green-800">{stats.reviewedApplications}</p>
            </div>
            <FaCheckCircle className="text-green-600" size={32} />
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Jobs with Apps</p>
              <p className="text-2xl font-bold text-purple-800">{stats.totalJobs}</p>
            </div>
            <FaBriefcase className="text-purple-600" size={32} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4">
          {/* Search */}
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by job title or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="hired">Hired</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Job Filter */}
          <select
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Jobs</option>
            {jobs.map(job => (
              <option key={job.id} value={job.id}>
                {job.jobTitle} ({job.applications.length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Jobs List */}
      <div className="space-y-4">
        {filteredJobs.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((job) => (
          <div key={job.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {/* Job Header */}
            <div
              className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => toggleJobExpansion(job.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-950">{job.jobTitle}</h3>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                    <span className="flex items-center">
                      <FaBriefcase className="mr-1" />
                      {job.companyName || job.employer?.companyName || 'Company Name'}
                    </span>
                    <span className="flex items-center">
                      <FaMapMarkerAlt className="mr-1" />
                      {job.parish}
                    </span>
                    <span className="flex items-center">
                      <FaClock className="mr-1" />
                      {job.employmentType}
                    </span>
                    {job.salary && (
                      <span className="flex items-center">
                        <FaDollarSign className="mr-1" />
                        {job.salary}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-950">{job.applications.length}</div>
                    <div className="text-xs text-gray-500">Application{job.applications.length !== 1 ? 's' : ''}</div>
                  </div>
                  <button className="text-blue-950">
                    {expandedJobs[job.id] ? '▲' : '▼'}
                  </button>
                </div>
              </div>
            </div>

            {/* Applications List */}
            {expandedJobs[job.id] && (
              <div className="p-4 bg-white">
                <div className="space-y-3">
                  {job.applications
                    .filter(app => filterStatus === 'all' || app.status === filterStatus)
                    .map((application) => (
                    <div
                      key={application.candidateId}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">
                              {application.candidate.firstName} {application.candidate.lastName}
                            </h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(application.status)}`}>
                              {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <FaUserAlt className="mr-2 text-gray-400" size={12} />
                              {application.candidate.email}
                            </div>
                            <div className="flex items-center">
                              <FaCalendar className="mr-2 text-gray-400" size={12} />
                              Applied: {new Date(application.appliedAt).toLocaleDateString()} at {new Date(application.appliedAt).toLocaleTimeString()}
                            </div>
                            {application.candidate.phone && (
                              <div className="flex items-center">
                                <span className="mr-2">📞</span>
                                {application.candidate.phone}
                              </div>
                            )}
                            {application.candidate.parish && (
                              <div className="flex items-center">
                                <FaMapMarkerAlt className="mr-2 text-gray-400" size={12} />
                                {application.candidate.parish}
                              </div>
                            )}
                          </div>

                          {/* Display answers if available */}
                          {application.answers && Object.keys(application.answers).length > 0 && (
                            <div className="mt-3 bg-blue-50 p-3 rounded">
                              <p className="text-xs font-medium text-blue-900 mb-2">Screening Responses:</p>
                              <div className="space-y-2">
                                {Object.entries(application.answers).map(([questionId, answer], index) => {
                                  const questionObj = application.jobQuestions
                                    ? Object.values(application.jobQuestions).find(q => q.id === questionId)
                                    : null;
                                  return (
                                    <div key={questionId}>
                                      <p className="text-xs font-semibold text-blue-900">
                                        Q{index + 1}: {questionObj?.question || `Question ${index + 1}`}
                                      </p>
                                      <p className="text-xs text-blue-800 mt-0.5">
                                        {answer || <span className="italic text-gray-400">No answer provided</span>}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="ml-4">
                          <Link
                            to={`/employer/resumes/${application.candidateId}`}
                            className="flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 transition-colors text-sm"
                          >
                            <FaEye className="mr-1" />
                            View Profile
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredJobs.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500 text-lg">No applications found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredJobs.length > 0 && (() => {
        const totalPages = Math.ceil(filteredJobs.length / pageSize);
        const start = (currentPage - 1) * pageSize + 1;
        const end = Math.min(currentPage * pageSize, filteredJobs.length);
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
          if (i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) {
            pages.push(i);
          } else if (pages[pages.length - 1] !== '...') {
            pages.push('...');
          }
        }
        return (
          <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              {[10, 25, 50].map(size => (
                <button
                  key={size}
                  onClick={() => setPageSize(size)}
                  className={`px-3 py-1 rounded border text-sm font-medium ${pageSize === size ? 'bg-blue-950 text-white border-blue-950' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  {size}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              Showing {start}–{end} of {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                {pages.map((page, i) =>
                  page === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 rounded border text-sm font-medium ${page === currentPage ? 'bg-blue-950 text-white border-blue-950' : 'border-gray-300 hover:bg-gray-50'}`}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default AdminJobApplications;
